import { z } from 'zod'

/** The systems hugo collects task-like signals from. Each has one connector. */
export const connectorIds = [
  'gmail',
  'calendar',
  'chat',
  'drive',
  'github',
  'jira',
  'confluence',
  'hubspot',
] as const
export const connectorIdSchema = z.enum(connectorIds)
export type ConnectorId = z.output<typeof connectorIdSchema>

export const connectorTitles: Record<ConnectorId, string> = {
  gmail: 'Gmail',
  calendar: 'Google Calendar',
  chat: 'Google Chat',
  drive: 'Google Drive',
  github: 'GitHub',
  jira: 'Jira',
  confluence: 'Confluence',
  hubspot: 'HubSpot',
}

/** Connectors on the user's Google account (better-auth's Google sign-in grants their scopes). */
export const googleConnectorIds: readonly ConnectorId[] = ['gmail', 'calendar', 'chat', 'drive']

/** Connectors that sign in with their own provider through hugo's OAuth flow (`/api/connect`). */
export const oauthConnectorIds: readonly ConnectorId[] = ['github']

/**
 * Connectors a user can connect today. The others are listed as coming later. An OAuth
 * connector also needs its provider's app on this server (see `oauthProviderConfigured`).
 */
export const availableConnectorIds: readonly ConnectorId[] = [
  ...googleConnectorIds,
  ...oauthConnectorIds,
]

export function isConnectorAvailable(id: ConnectorId): boolean {
  return availableConnectorIds.includes(id)
}

/** active: syncing. error: the last sync failed. disconnected: the user disconnected it. */
export const connectionStatuses = ['active', 'error', 'disconnected'] as const
export const connectionStatusSchema = z.enum(connectionStatuses)
export type ConnectionStatus = z.output<typeof connectionStatusSchema>

/**
 * Where a connection's OAuth token comes from: `google` is the user's linked Google account
 * (better-auth, encrypted at rest), `oauth` hugo's own grant from another provider (GitHub,
 * Atlassian, HubSpot; encrypted in `connector_tokens`), `fake` the fake services of the test
 * stack. Never the token.
 */
export const connectionCredentials = ['google', 'oauth', 'fake'] as const
export const connectionCredentialsSchema = z.enum(connectionCredentials)
export type ConnectionCredentials = z.output<typeof connectionCredentialsSchema>

/**
 * GitHub's issue states as hugo sees them (decision 108): open, in the Testing column of a
 * GitHub Projects board, or closed.
 */
export const githubDoneWhen = ['testing_or_closed', 'closed'] as const
export const githubChoices = ['testing', 'close', 'hugo'] as const
export const githubChoiceSchema = z.enum(githubChoices)
export type GithubChoice = z.output<typeof githubChoiceSchema>

/** The name of a column (a Status option) on a GitHub Projects board. */
const statusName = z.string().trim().min(1).max(60)

/**
 * The user's GitHub options (decision 108). `testingStatus`: the Projects board column that means
 * "testing" (matched by name, ignoring case). `doneWhen`: whether the Testing column already makes an
 * assigned item done in hugo, or only closing the issue does (closing always does). `lastChoice`: what "done" in hugo did on GitHub last time (the dialog's
 * default).
 */
export const githubOptionsSchema = z.object({
  testingStatus: statusName.default('Testing'),
  doneWhen: z.enum(githubDoneWhen).default('testing_or_closed'),
  // The first time nothing changes on GitHub unless the user picks it (Enter is safe).
  lastChoice: githubChoiceSchema.default('hugo'),
})
export type GithubOptions = z.output<typeof githubOptionsSchema>
export const githubOptionsPatchSchema = z
  .object({
    testingStatus: statusName,
    doneWhen: z.enum(githubDoneWhen),
    lastChoice: githubChoiceSchema,
  })
  .partial()
export type GithubOptionsPatch = z.output<typeof githubOptionsPatchSchema>

/** A connection's options, read leniently: what does not parse reads as its default. */
export function githubOptionsOf(raw: unknown): GithubOptions {
  const parsed = githubOptionsSchema.safeParse(raw ?? {})
  return parsed.success ? parsed.data : githubOptionsSchema.parse({})
}

/** A user's connection to one connector, as clients see it (no cursor, no credentials). */
export const connectionSchema = z.object({
  id: z.uuid(),
  connector: connectorIdSchema,
  status: connectionStatusSchema,
  accountEmail: z.string(),
  lastSyncAt: z.iso.datetime().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.iso.datetime(),
  /** GitHub only (decision 108); null for the others. */
  github: githubOptionsSchema.nullable().default(null),
  /**
   * The granted access lacks something a feature needs (GitHub's `project` scope for the Testing
   * column): connecting again asks for it.
   */
  missingScopes: z.array(z.string()).default([]),
})
export type Connection = z.output<typeof connectionSchema>

/** One connector in the settings list, with the user's connection if there is one. */
export const connectorInfoSchema = z.object({
  id: connectorIdSchema,
  title: z.string(),
  available: z.boolean(),
  connection: connectionSchema.nullable(),
})
export type ConnectorInfo = z.output<typeof connectorInfoSchema>
